import { useMutation } from "@tanstack/react-query";
import { QueryKeys, client, queryClient } from "queryClient";
import { CreateWebsiteInput, InfiniteWebsiteData } from "types";
import { getWebsite } from "modules/websites/api";

export const useWebsiteCreate = () => {
  const createMutation = useMutation({
    mutationFn: async (input: CreateWebsiteInput) => {
      const { data: newWebsite } = await client.models.Website.create(input);

      if (!newWebsite) return null;

      const website = await getWebsite(newWebsite.id);

      return website;
    },
    // When mutate is called:
    onMutate: async (newWebsite) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [QueryKeys.Websites] });

      // Snapshot the previous value
      const previousWebsites = queryClient.getQueryData([QueryKeys.Websites]);

      // Optimistically update to the new value
      if (previousWebsites) {
        queryClient.setQueryData(
          [QueryKeys.Websites],
          (old: InfiniteWebsiteData) => {
            const oldData = old?.pages
              ? old.pages.flatMap((page) => page.websiteList)
              : [];

            [...oldData, newWebsite];
          },
        );
      }

      // Return a context object with the snapshotted value
      return { previousWebsites };
    },
    // If the mutation fails,
    // use the context returned from onMutate to rollback
    onError: (err, newWebsite, context) => {
      console.error("Error saving record:", err, newWebsite);
      if (context?.previousWebsites) {
        queryClient.setQueryData(
          [QueryKeys.Websites],
          context.previousWebsites,
        );
      }
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Websites] });
    },
  });

  return createMutation;
};
